/** @format */

import {Trans, useTranslation} from 'react-i18next';

import {Box} from '@mui/material';

import {useTheme} from '@mui/material/styles';

import {AccountsCompanyMediaplanType} from 'graphql/generated/types';

import {ColumnType} from 'components/Table/core/types';

import {divideBySpaces} from 'helpers/numberHelpers';


import {FormattedCell} from '../components/FormattedCell';
import {FormattedCellSubRow} from '../components/FormattedCellSubRow';
import {KpiHead} from '../components/KpiHead';
import {KPI_WIDTH} from '../constants';
import {calcKpiColumnSize} from '../helpers/calcKpiColumnSize';
import {getKpiTitle} from '../helpers/getKpiTitle';
import {KpiType} from '../types';

export interface GetColumnsOptions {
  primaryKpi?: KpiType;
  showSensorGroups: boolean;
  sensorColumnWidth?: number;
  mediaplanType?: AccountsCompanyMediaplanType | null;
}

const useGetColumns = ({
  primaryKpi,
  showSensorGroups,
  sensorColumnWidth,
  mediaplanType,
}: GetColumnsOptions): ColumnType[] => {
  const {t} = useTranslation();
  const theme = useTheme();

  let columns: ColumnType[] = [
    {
      id: 'title',
      title: <Trans>Название</Trans>,
      size: 300,
      sortingFn: 'text',
      order: 0,
    },
    {
      id: 'id',
      title: 'ID',
      size: 120,
      sortingFn: 'alphanumeric',
      order: 10,
    },
    {
      id: 'subAccount',
      title: <Trans>Субаккаунт</Trans>,
      size: 150,
      sortingFn: 'text',
      order: 20,
    },
    showSensorGroups && sensorColumnWidth !== undefined
      ? {
          id: 'sensors',
          title: <Trans>Сенсоры</Trans>,
          order: 30,
          sortingFn: 'alphanumeric',
          children: [
            {
              id: 'red',
              title: (
                <Box sx={{color: '#EA5759'}} component='span'>
                  <Trans>Критичные</Trans>
                </Box>
              ),
              size: sensorColumnWidth,
              sortingFn: 'alphanumeric',
              cell: FormattedCellSubRow,
            },
            {
              id: 'yellow',
              title: (
                <Box sx={{color: '#DDA14D'}} component='span'>
                  <Trans>Обычные</Trans>
                </Box>
              ),
              size: sensorColumnWidth,
              sortingFn: 'alphanumeric',
              cell: FormattedCellSubRow,
            },
            {
              id: 'gray',
              title: (
                <Box
                  sx={{color: theme.palette.semantic_dark?.surface.onSurfaceMuted}}
                  component='span'
                >
                  <Trans>Некритичные</Trans>
                </Box>
              ),
              size: sensorColumnWidth,
              sortingFn: 'alphanumeric',
              cell: FormattedCellSubRow,
            },
          ],
        }
      : {
          id: 'sensors',
          title: <Trans>Срабатывания сенсоров</Trans>,
          order: 30,
          size: sensorColumnWidth,
          sortingFn: 'alphanumeric',
          cell: FormattedCellSubRow,
        },
  ];

  // Эти колонки показываются всегда.
  // Но если одна из них является основным KPI, то она может быть исключена (см. логику ниже).

  const columnsProperties: ColumnType[] = [
    {
      id: 'spent',
      title: `${t('Расход')}, ₽`,
      size: KPI_WIDTH,
      sortingFn: 'alphanumeric',
      order: 40,
      cell: FormattedCell,
    },
    {
      id: 'impressions',
      title: <Trans>Показы</Trans>,
      size: KPI_WIDTH,
      sortingFn: 'alphanumeric',
      order: 60,
      cell: FormattedCell,
    },
    {
      id: 'clicks',
      title: <Trans>Клики</Trans>,
      size: KPI_WIDTH,
      sortingFn: 'alphanumeric',
      order: 70,
      cell: FormattedCell,
    },
    {
      id: 'ctr',
      title: 'CTR, %',
      size: KPI_WIDTH,
      sortingFn: 'alphanumeric',
      order: 80,
      cell: FormattedCell,
    },
    {
      id: 'cpc',
      title: 'CPC, ₽',
      size: KPI_WIDTH,
      sortingFn: 'alphanumeric',
      order: 90,
      cell: FormattedCell,
    },
  ];

  // Если тип кампании медийка, то добавляем CPM

  if (mediaplanType === 'MEDIA') {
    columnsProperties.push({
      id: 'cpm',
      title: 'CPM, ₽',
      size: KPI_WIDTH,
      sortingFn: 'alphanumeric',
      order: 100,
      cell: FormattedCell,
    });
  }

  // Если указан основной KPI и у него есть план, то показываем дополнительную колонку.
  // Если этот KPI есть в columnsProperties, то исключаем его.

  const planParsed = parseInt(primaryKpi?.value);
  const plan = Number.isNaN(planParsed) ? undefined : planParsed;

  if (primaryKpi && plan !== undefined) {
    const primaryKpiId = primaryKpi.name.toLowerCase();
    const formattedPlan = divideBySpaces(plan);
    const title = getKpiTitle(primaryKpi.name, t);

    // Если ширина текста оказалась больше минимальной, то ширина колонки будет увеличена
    const size = calcKpiColumnSize(title.length, formattedPlan.length);

    const kpiColumn: ColumnType = {
      id: primaryKpiId,
      title: <KpiHead title={title} plan={formattedPlan} />,
      size,
      sortingFn: 'alphanumeric',
      order: 50,
      cell: FormattedCell,
    };

    const filteredProperties = columnsProperties.filter(column => column.id !== primaryKpiId);
    columns = [...columns, ...filteredProperties, kpiColumn];
  } else {
    columns = [...columns, ...columnsProperties];
  }

  return columns;
};

export {useGetColumns};
